<script lang="ts">
  /**
   * The player's accent colour: a row of named swatches and a "Custom" one
   * that opens two sliders (hue, vividness). Whatever is chosen is drawn
   * through the accent engine (`accent.ts`), so every swatch shows the colour
   * the player will actually get on this base, and a small preview (a filled
   * button and a switch that is on) shows it in use. When the engine had to
   * move a custom colour to keep it readable, a note says so.
   *
   * - The swatches are one `radiogroup` (`radioKeys.ts`): one Tab stop, the
   *   arrow keys move and select. The chosen one carries a check and a ring,
   *   and its name is written under the row, so the choice is never colour
   *   alone.
   * - The sliders are real `<input type="range">`s: keyboard, screen reader
   *   and drag work the way the platform makes them. `oninput` reports every
   *   move (to show it live), `onchange` the colour when a drag ends (to save
   *   it). A preset reports through `onchange` at once.
   *   The hue slider speaks its value as degrees and a colour ("210°, blue",
   *   `hueName`, default the shell's words for `hueFamily`).
   * - The parent owns `value` (a hex: a preset's `hex` or a custom colour)
   *   and `base`. Nothing is stored here.
   *
   * - `signature` (since 1.4.0): the game's own colour, named after the
   *   game, drawn as the FIRST swatch, before the shared presets
   *   (`presetsWithSignature`: a shared preset that would look the same
   *   gives way). It is the game's default: a new player's `value` is its
   *   `hex`. Its name is the game's name, not translated.
   *
   * Every word is a prop with an English default (`shellText`); preset names
   * come from `presetName` (default: the preset's English `name`).
   *
   * Test ids: `testid` on the block; `testids.swatch` is a prefix, each
   * preset's swatch is `<prefix>-<id>` (the signature's id is `signature`
   * unless it names one); `testids.custom`, `.hue`,
   * `.vividness`, `.preview`, `.note`, `.name` on their parts. No defaults.
   */
  import {
    ACCENT_PRESETS,
    type AccentBase,
    type AccentGrounds,
    type AccentPreset,
    type AccentSignature,
    findPreset,
    type HueFamily,
    hueFamily,
    hueTrack,
    hueWheel,
    pickerColour,
    pickerPosition,
    presetsToResolve,
    presetsWithSignature,
    resolveAccent,
    vividnessTrack,
  } from '../accent.js';
  import { type ShellStringKey, shellText } from '../i18n.svelte.js';
  import { handleRadioKeydown, rovingTabIndex } from '../radioKeys.js';
  import { styleProps } from '../styleProps.js';
  import Icon from './Icon.svelte';
  import PillButton from './PillButton.svelte';
  import Switch from './Switch.svelte';

  interface Props {
    /** The colour chosen: a preset's `hex` or a custom `#rrggbb`. */
    readonly value: string;
    /** The base the game shows: the swatches and the preview are drawn for it. */
    readonly base: AccentBase;
    /** A colour chosen: a preset pressed, or a slider let go. Save it here. */
    readonly onchange: (hex: string) => void;
    /** Every slider move, before it is let go: show it live here (optional). */
    readonly oninput?: ((hex: string) => void) | undefined;
    /** The group's accessible name. Default "Accent colour". */
    readonly label?: string | undefined;
    /** The shared presets (default `ACCENT_PRESETS`), after the signature. */
    readonly presets?: readonly AccentPreset[];
    /**
     * The game's signature colour, the first swatch and the game's default
     * (since 1.4.0). Absent: the presets alone, as in 1.3.0.
     */
    readonly signature?: AccentSignature | undefined;
    /** A preset's name in the game's words. Default its English `name`. */
    readonly presetName?: ((preset: AccentPreset) => string) | undefined;
    readonly customLabel?: string | undefined;
    readonly hueLabel?: string | undefined;
    /**
     * The everyday colour name of a hue (0..360), spoken after the degrees
     * ("210°, blue"). Default the shell's words for `hueFamily(hue)`.
     */
    readonly hueName?: ((hue: number) => string) | undefined;
    readonly vividnessLabel?: string | undefined;
    /** Shown when a custom colour was moved to stay readable. */
    readonly adjustedNote?: string | undefined;
    /** The preview button's word. Default "Play". */
    readonly previewAction?: string | undefined;
    /** The preview switch's name. Default "On". */
    readonly previewToggle?: string | undefined;
    /** The ladder the engine reads against. Default the house theme's. */
    readonly grounds?: AccentGrounds | undefined;
    /** The words on the results gradient, if not white. */
    readonly onDeep?: string | undefined;
    readonly testid?: string | undefined;
    readonly testids?:
      | {
          readonly swatch?: string;
          readonly custom?: string;
          readonly hue?: string;
          readonly vividness?: string;
          readonly preview?: string;
          readonly note?: string;
          readonly name?: string;
        }
      | undefined;
  }
  const {
    value,
    base,
    onchange,
    oninput,
    label,
    presets: shared = ACCENT_PRESETS,
    signature,
    presetName,
    customLabel,
    hueLabel,
    hueName,
    vividnessLabel,
    adjustedNote,
    previewAction,
    previewToggle,
    grounds,
    onDeep,
    testid,
    testids,
  }: Props = $props();

  const HUE_WORDS: Readonly<Record<HueFamily, ShellStringKey>> = {
    pink: 'huePink',
    red: 'hueRed',
    orange: 'hueOrange',
    yellow: 'hueYellow',
    green: 'hueGreen',
    teal: 'hueTeal',
    blue: 'hueBlue',
    purple: 'huePurple',
  };
  const nameOfHue = (hue: number): string => hueName?.(hue) ?? shellText(HUE_WORDS[hueFamily(hue)]);

  const uid = $props.id();
  let group = $state<HTMLDivElement | undefined>();

  /** Custom pressed while a preset is the value: the sliders open on that colour. */
  let customAsked = $state(false);
  /** The colour while a slider moves, before it is let go. */
  let live = $state<string | null>(null);
  /**
   * The sliders' own position for the colour they last made (no round trip
   * through hex, which would nudge them). Dropped once the value is another.
   */
  let held = $state<{
    readonly hue: number;
    readonly vividness: number;
    readonly hex: string;
  } | null>(null);

  /** The row: the signature first, then the shared presets that look unlike it. */
  const row = $derived(presetsWithSignature(signature, shared));
  /** What the value is looked up in: the row, then the presets that gave way to the signature. */
  const lookup = $derived(presetsToResolve(signature, shared));
  const stored = $derived(findPreset(value, lookup));
  /**
   * The swatches: the row, and a preset that gave way to the signature kept
   * in its place while it is the player's value (it is theirs, not custom).
   */
  const presets = $derived(
    signature !== undefined && stored !== null && !row.includes(stored)
      ? [row[0] ?? stored, ...shared.filter((each) => row.includes(each) || each === stored)]
      : row,
  );
  const options = $derived({ grounds, onDeep, presets: lookup });
  const preset = $derived(live === null ? findPreset(value, presets) : null);
  const customOn = $derived(preset === null || customAsked);
  const shown = $derived(resolveAccent(live ?? value, base, options));
  const swatches = $derived(
    presets.map((each) => ({ preset: each, colour: resolveAccent(each.hex, base, options) })),
  );
  const position = $derived(
    held !== null && held.hex === (live ?? value) ? held : pickerPosition(shown.accent, base),
  );
  const checkedIndex = $derived(
    customOn ? presets.length : presets.findIndex((each) => each === preset),
  );
  const nameOf = (each: AccentPreset): string => presetName?.(each) ?? each.name;
  const chosenName = $derived(
    customOn || preset === null ? (customLabel ?? shellText('accentCustom')) : nameOf(preset),
  );

  function choosePreset(each: AccentPreset): void {
    customAsked = false;
    live = null;
    held = null;
    if (findPreset(value, presets) !== each) onchange(each.hex);
  }

  function chooseCustom(): void {
    customAsked = true;
  }

  function onKeydown(event: KeyboardEvent): void {
    handleRadioKeydown(event, group, checkedIndex, presets.length + 1, (index) => {
      const each = presets[index];
      if (each === undefined) chooseCustom();
      else choosePreset(each);
    });
  }

  function move(next: { readonly hue: number; readonly vividness: number }, commit: boolean): void {
    const hex = pickerColour(next.hue, next.vividness, base);
    held = { ...next, hex };
    if (commit) {
      live = null;
      customAsked = true;
      onchange(hex);
    } else {
      live = hex;
      oninput?.(hex);
    }
  }

  function read(event: Event): number {
    return Number((event.currentTarget as HTMLInputElement).value);
  }
</script>

<div class="ui-accent" data-testid={testid}>
  <div
    class="ui-accent-swatches"
    role="radiogroup"
    aria-label={label ?? shellText('accentLabel')}
    bind:this={group}
  >
    {#each swatches as swatch, index (swatch.preset.id)}
      <button
        type="button"
        role="radio"
        class="ui-accent-swatch"
        aria-checked={checkedIndex === index}
        aria-label={nameOf(swatch.preset)}
        tabindex={rovingTabIndex(index, checkedIndex)}
        data-testid={testids?.swatch === undefined
          ? undefined
          : `${testids.swatch}-${swatch.preset.id}`}
        onclick={() => choosePreset(swatch.preset)}
        onkeydown={onKeydown}
      >
        <span
          class="ui-accent-dot"
          use:styleProps={{ background: swatch.colour.accent, color: swatch.colour.onAccent }}
        >
          {#if checkedIndex === index}<Icon name="check" size={16} />{/if}
        </span>
      </button>
    {/each}
    <button
      type="button"
      role="radio"
      class="ui-accent-swatch"
      aria-checked={customOn}
      aria-label={customLabel ?? shellText('accentCustom')}
      aria-controls={customOn ? `${uid}-custom` : undefined}
      tabindex={rovingTabIndex(presets.length, checkedIndex)}
      data-testid={testids?.custom}
      onclick={chooseCustom}
      onkeydown={onKeydown}
    >
      <span
        class="ui-accent-dot"
        use:styleProps={{
          background: customOn ? shown.accent : hueWheel(base),
          color: shown.onAccent,
        }}
      >
        {#if customOn}<Icon name="check" size={16} />{/if}
      </span>
    </button>
  </div>

  <div class="ui-accent-foot">
    <p class="ui-accent-name" data-testid={testids?.name}>{chosenName}</p>
    <!-- What the colour looks like in use. Drawn with the parts themselves,
         recoloured here, and out of reach: it is a picture, not controls. -->
    <div
      class="ui-accent-preview"
      aria-hidden="true"
      inert
      data-testid={testids?.preview}
      data-accent={shown.accent}
      use:styleProps={{
        '--ui-accent': shown.accent,
        '--ui-accent-deep': shown.accentDeep,
        '--ui-on-accent': shown.onAccent,
      }}
    >
      <PillButton
        label={previewAction ?? shellText('accentPreviewAction')}
        size="sm"
        onclick={() => undefined}
      />
      <Switch
        label={previewToggle ?? shellText('accentPreviewToggle')}
        checked
        onchange={() => undefined}
      />
    </div>
  </div>

  {#if customOn}
    <div class="ui-accent-custom" id={`${uid}-custom`}>
      <label class="ui-accent-slider">
        <span class="ui-accent-slider-label">{hueLabel ?? shellText('accentHue')}</span>
        <input
          class="ui-accent-range"
          type="range"
          min="0"
          max="359"
          step="1"
          value={position.hue}
          aria-valuetext={`${String(position.hue)}°, ${nameOfHue(position.hue)}`}
          use:styleProps={{
            '--ui-accent-track': hueTrack(base),
            '--ui-accent-thumb': shown.accent,
          }}
          data-testid={testids?.hue}
          oninput={(event) => move({ hue: read(event), vividness: position.vividness }, false)}
          onchange={(event) => move({ hue: read(event), vividness: position.vividness }, true)}
        />
      </label>
      <label class="ui-accent-slider">
        <span class="ui-accent-slider-label">{vividnessLabel ?? shellText('accentVividness')}</span>
        <input
          class="ui-accent-range"
          type="range"
          min="0"
          max="100"
          step="1"
          value={Math.round(position.vividness * 100)}
          aria-valuetext={`${String(Math.round(position.vividness * 100))} %`}
          use:styleProps={{
            '--ui-accent-track': vividnessTrack(position.hue, base),
            '--ui-accent-thumb': shown.accent,
          }}
          data-testid={testids?.vividness}
          oninput={(event) => move({ hue: position.hue, vividness: read(event) / 100 }, false)}
          onchange={(event) => move({ hue: position.hue, vividness: read(event) / 100 }, true)}
        />
      </label>
    </div>
  {/if}

  <!-- Polite and always present, so the note is announced when it appears. -->
  <p class="ui-accent-note" aria-live="polite" data-testid={testids?.note}>
    {#if shown.adjusted}{adjustedNote ?? shellText('accentAdjusted')}{/if}
  </p>
</div>

<style>
  .ui-accent {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-2);
    min-width: 0;
    width: 100%;
    color: var(--ui-ink);
  }

  .ui-accent-swatches {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(2.75rem, 1fr));
    justify-items: center;
    gap: var(--ui-space-1);
  }

  /* The target is 2.75rem; the colour a 2rem circle inside it. */
  .ui-accent-swatch {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
  }

  .ui-accent-swatch:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 0;
  }

  .ui-accent-dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
  }

  /* Chosen: a ring in the ink with a gap, as well as the check. */
  .ui-accent-swatch[aria-checked='true'] .ui-accent-dot {
    outline: 2px solid var(--ui-ink);
    outline-offset: 2px;
  }

  .ui-accent-foot {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--ui-space-2);
    min-width: 0;
  }

  .ui-accent-name {
    margin: 0;
    min-width: 0;
    font: var(--ui-text-label);
    color: var(--ui-ink-2);
  }

  .ui-accent-preview {
    display: inline-flex;
    align-items: center;
    gap: var(--ui-space-3);
    padding: var(--ui-space-1) var(--ui-space-3);
    border-radius: var(--ui-radius-pill);
    background: var(--ui-sunken);
  }

  .ui-accent-custom {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-2);
  }

  .ui-accent-slider {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-1);
    min-width: 0;
  }

  .ui-accent-slider-label {
    font: var(--ui-text-caption);
    color: var(--ui-ink-2);
  }

  .ui-accent-range {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    min-width: 0;
    height: 2.75rem;
    margin: 0;
    background: transparent;
  }

  .ui-accent-range::-webkit-slider-runnable-track {
    height: 0.75rem;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-accent-track);
  }

  .ui-accent-range::-moz-range-track {
    height: 0.75rem;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-accent-track);
  }

  /* The thumb is the colour itself, in a white ring with a dark edge, so it
     shows on every part of either track. */
  .ui-accent-range::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    box-sizing: border-box;
    width: 1.5rem;
    height: 1.5rem;
    margin-top: -0.375rem;
    border-radius: 50%;
    border: 3px solid var(--ui-on-deep);
    background: var(--ui-accent-thumb);
    box-shadow: 0 0 0 1px rgb(0 0 0 / 0.55);
  }

  .ui-accent-range::-moz-range-thumb {
    box-sizing: border-box;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    border: 3px solid var(--ui-on-deep);
    background: var(--ui-accent-thumb);
    box-shadow: 0 0 0 1px rgb(0 0 0 / 0.55);
  }

  .ui-accent-range:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
    border-radius: var(--ui-radius-pill);
  }

  .ui-accent-note {
    margin: 0;
    font: var(--ui-text-caption);
    color: var(--ui-ink-2);
  }

  /* Empty, it takes no room (and still exists, so its words are announced). */
  .ui-accent-note:empty {
    margin-top: calc(-1 * var(--ui-space-2));
  }
</style>
